import React, { useState } from "react";
import { Link } from "react-router-dom";
import useSEO from "../../lib/useSEO";
import { SeoShell, SeoHero, PlanCTA } from "./SeoLayout";
import { LinkToThis } from "./Tools";
import { postJson } from "../../lib/growthApi";

const QUESTIONS = [
  ["Testing", "Are all critical and high defects closed or accepted with a workaround?", 3, "erp-test-management"],
  ["Testing", "Has UAT passed at or above your agreed threshold?", 3, "erp-test-management"],
  ["Testing", "Have performance, security and role tests passed?", 2, "erp-test-management"],
  ["Data", "Have you completed at least two mock loads with reconciled counts and values?", 3, "data-migration-testing"],
  ["Data", "Have data owners signed off every migration object?", 2, "data-migration-testing"],
  ["Cutover", "Is the cutover runbook frozen, with an owner and backup for every task?", 3, "erp-cutover-plan"],
  ["Cutover", "Did the final mock cutover finish inside the planned window?", 2, "erp-cutover-plan"],
  ["Cutover", "Is the rollback plan written and rehearsed?", 2, "erp-cutover-plan"],
  ["People", "Have more than 90% of end users completed training?", 2, "erp-go-live-checklist"],
  ["People", "Are super-users and floor-walkers rostered for go-live week?", 1, "erp-go-live-checklist"],
  ["Support", "Is the hypercare team, SLA and escalation matrix live?", 2, "hypercare-plan"],
  ["Governance", "Has the steering committee approved written Go/No-Go criteria?", 3, "erp-go-live-checklist"],
];
const OPTS = [["Yes", 1], ["Partly", 0.5], ["No", 0]];

function verdict(score, critGaps) {
  if (critGaps > 0 || score < 60) return ["Not ready", "#B91C1C", "Critical gaps remain. Treat this as a No-Go until they are closed."];
  if (score < 85) return ["At risk", "#B45309", "You are close. Close the gaps below before your final Go/No-Go."];
  return ["Ready", "#047857", "Strong position. Keep evidence for each answer ready for the steering committee."];
}

function ReportEmail({ answers }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState({ busy: false, err: "", sent: null });
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state.busy) return;
    setState({ busy: true, err: "", sent: null });
    try {
      const d = await postJson("/leads/readiness", { email: email.trim().toLowerCase(), answers: QUESTIONS.map((_, i) => answers[i]), website });
      setState({ busy: false, err: d.sent ? "" : "We couldn't send the email right now. Please try again later.", sent: d.sent });
    } catch (ex) { setState({ busy: false, err: ex.message, sent: null }); }
  };
  if (state.sent) return <p className="mt-5 text-sm text-emerald-700 font-semibold" data-testid="readiness-email-sent">Report sent. Check your inbox in the next minute.</p>;
  return (
    <form onSubmit={submit} className="mt-5 pt-5 border-t" style={{ borderColor: "#F0D3D8" }} data-testid="readiness-email-form">
      <div className="text-sm font-semibold text-[#0F1723]">Email me this report with the fixes for each gap</div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={(e) => setWebsite(e.target.value)} className="absolute -left-[9999px] w-px h-px opacity-0" name="website"/>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
          className="flex-1 border rounded-sm py-2.5 px-3 text-sm outline-none focus:border-zinc-500" style={{ borderColor: "#E5C7CC" }} data-testid="readiness-email-input"/>
        <button type="submit" disabled={state.busy} className="px-4 py-2.5 rounded-sm text-white text-sm font-semibold" style={{ background: "#7A1F2B" }} data-testid="readiness-email-submit">
          {state.busy ? "Sending…" : "Email my report"}
        </button>
      </div>
      {state.err && <p className="mt-2 text-xs text-red-600" data-testid="readiness-email-error">{state.err}</p>}
    </form>
  );
}

function Result({ answers }) {
  const max = QUESTIONS.reduce((s, q) => s + q[2], 0);
  const got = QUESTIONS.reduce((s, q, i) => s + q[2] * (answers[i] ?? 0), 0);
  const score = Math.round((got / max) * 100);
  const gaps = QUESTIONS.map((q, i) => ({ q, a: answers[i] })).filter((x) => x.a < 1);
  const crit = gaps.filter((x) => x.q[2] === 3 && x.a === 0).length;
  const [label, color, msg] = verdict(score, crit);
  return (
    <div className="mt-8 p-6 rounded-md bg-white border" style={{ borderColor: color }} data-testid="readiness-result">
      <div className="flex items-baseline gap-4 flex-wrap">
        <div className="font-display text-5xl font-extrabold" style={{ color }} data-testid="readiness-score">{score}%</div>
        <div className="font-display text-2xl font-extrabold" style={{ color }} data-testid="readiness-verdict">{label}</div>
      </div>
      <p className="mt-2 text-sm text-zinc-700">{msg}</p>
      {gaps.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="readiness-gaps">
          {gaps.map(({ q, a }) => (
            <li key={q[1]} className="text-sm flex gap-2"><span className="font-mono text-[10px] uppercase tracking-widest mt-0.5 w-20 shrink-0" style={{ color: a === 0 ? "#B91C1C" : "#B45309" }}>{q[0]}</span>
              <span>{q[1]} <Link to={`/guides/${q[3]}`} className="underline" style={{ color: "#7A1F2B" }}>How to fix</Link></span></li>
          ))}
        </ul>
      )}
      <ReportEmail key={JSON.stringify(answers)} answers={answers} />
    </div>
  );
}

export default function ReadinessCalculator() {
  useSEO({
    title: "Free ERP Go-Live Readiness Calculator (SAP, Oracle, Workday) | Capittance",
    description: "Score your ERP go-live readiness in two minutes. 12 weighted checks across testing, data, cutover, people, support and governance, with the gaps to close.",
    path: "/tools/go-live-readiness-calculator",
    jsonLd: { "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Go-Live Readiness Calculator", applicationCategory: "BusinessApplication", operatingSystem: "Web", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } },
  });
  const [answers, setAnswers] = useState({});
  const done = Object.keys(answers).length === QUESTIONS.length;
  return (
    <SeoShell>
      <SeoHero eyebrow="Free tool · 2 minutes" h1="Go-live readiness calculator" intro="Twelve weighted checks used in real Go/No-Go meetings. Answer honestly and see where you stand." />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <ol className="space-y-3" data-testid="readiness-questions">
          {QUESTIONS.map((q, i) => (
            <li key={q[1]} className="p-4 rounded-md bg-white border flex flex-col sm:flex-row sm:items-center gap-3 justify-between" style={{ borderColor: "#F0D3D8" }}>
              <div><span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#7A1F2B" }}>{q[0]}</span><div className="text-[15px] text-[#0F1723]">{q[1]}</div></div>
              <div className="flex gap-1 shrink-0">
                {OPTS.map(([l, v]) => (
                  <button key={l} onClick={() => setAnswers((a) => ({ ...a, [i]: v }))}
                    className="px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors"
                    style={answers[i] === v ? { background: "#7A1F2B", color: "#fff", borderColor: "#7A1F2B" } : { borderColor: "#E5C7CC" }}
                    data-testid={`readiness-q${i}-${l.toLowerCase()}`}>{l}</button>
                ))}
              </div>
            </li>
          ))}
        </ol>
        {done ? <Result answers={answers} /> : <p className="mt-6 text-sm text-zinc-500" data-testid="readiness-progress">{Object.keys(answers).length} of {QUESTIONS.length} answered</p>}
        <LinkToThis path="/tools/go-live-readiness-calculator" name="Go-Live Readiness Calculator" />
        <PlanCTA id="readiness" />
      </main>
    </SeoShell>
  );
}
